import { useEffect } from 'react';

import { registrarPresenca } from 'services/qualidadeService';
import { temSessao } from 'services/sessao';

/** No máximo uma chamada por hora: o backend já grava uma vez por dia, isto só poupa a rede. */
export const INTERVALO_PRESENCA_MS = 60 * 60 * 1000;
const CHAVE = 'presencaEnviadaEm';

let ultimaEnvio = 0;

const lerUltimo = () => {
  try {
    return Math.max(ultimaEnvio, Number(window.localStorage.getItem(CHAVE)) || 0);
  } catch {
    return ultimaEnvio;
  }
};

const gravarUltimo = (quando) => {
  ultimaEnvio = quando;
  try {
    window.localStorage.setItem(CHAVE, String(quando));
  } catch {
    // Armazenamento bloqueado: a memória do módulo basta para esta sessão.
  }
};

/**
 * Envia a presença se a tela está visível, há sessão e passou o intervalo. Exportada para os testes.
 *
 * É o sinal de atividade da regra de inatividade (C7), e não o /login/refresh: a renovação acontece
 * quando qualquer chamada da API toma 401, inclusive o polling da tela de pagamento numa aba ou num app
 * em segundo plano. A presença só sai com a tela VISÍVEL.
 */
export function enviarPresencaSeVisivel(agora = Date.now()) {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return false;
  if (!temSessao()) return false;
  if (agora - lerUltimo() < INTERVALO_PRESENCA_MS) return false;
  gravarUltimo(agora);
  registrarPresenca().catch(() => {
    // Falhou: libera para a próxima oportunidade, sem incomodar ninguém.
    gravarUltimo(0);
  });
  return true;
}

/** Para os testes começarem do zero. */
export function reiniciarPresenca() {
  gravarUltimo(0);
}

/**
 * Presença com a tela visível (C7): ao abrir o app ou o site, ao voltar para a aba e quando o app volta
 * ao primeiro plano (Capacitor). É o que mantém o perfil na busca e o que o devolve se ele saiu por
 * inatividade.
 */
export default function usePresenca(logado) {
  useEffect(() => {
    if (!logado) return undefined;

    enviarPresencaSeVisivel();
    const aoMudarVisibilidade = () => enviarPresencaSeVisivel();
    document.addEventListener('visibilitychange', aoMudarVisibilidade);

    let removerApp;
    let cancelado = false;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor?.isNativePlatform?.()) return;
        const { App } = await import('@capacitor/app');
        const inscricao = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) enviarPresencaSeVisivel();
        });
        removerApp = () => inscricao?.remove?.();
        if (cancelado) removerApp();
      } catch {
        // Sem Capacitor (navegador, Jest): o visibilitychange já cobre.
      }
    })();

    return () => {
      cancelado = true;
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
      removerApp?.();
    };
  }, [logado]);
}
